import { useRef, useState } from 'react';
import { DeferredChoiceMenu as ChoiceMenu } from '~/ui/deferred/deferred-menus';
import { FormattedMessage, useIntl } from 'react-intl';
import { FilterTrigger } from './filter-trigger';
import { pick } from './pick';

export type ExperienceOption = {
  id: string;
  name: string;
};

type ExperienceFilterProps = {
  value: string[];
  options: ExperienceOption[];
  onChange: (value: string[]) => void;
  /** Fired when the menu opens, for analytics. */
  onOpen?: () => void;
};

/**
 * Multi-select experience filter. Same shape as the other filters, for the
 * upcoming-events page's bar — that page lists events across every experience,
 * so narrowing to one or two of them is the filter visitors reach for most.
 * (Home has no equivalent: its cards *are* the experiences.)
 *
 * Selections are committed via `onChange` on EVERY toggle, not on menu close,
 * so the URL — the committed source of truth the listbox reads back from —
 * stays in step with what the visitor sees.
 */
export function ExperienceFilter({
  value,
  options,
  onChange,
  onOpen,
}: ExperienceFilterProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const intl = useIntl();

  const selected = pick(options, value);

  return (
    <>
      <FilterTrigger
        ref={triggerRef}
        label={
          <FormattedMessage
            defaultMessage="Experience"
            description="Label on the experience filter button in the upcoming events filter bar"
          />
        }
        value={
          selected.length === 0 || selected.length === options.length ? (
            <FormattedMessage
              defaultMessage="All"
              description="Placeholder on the experience filter button when no experiences are selected"
            />
          ) : selected.length === 1 ? (
            selected[0]!.name
          ) : (
            <FormattedMessage
              defaultMessage="{count} experiences"
              description="Summary on the experience filter button when multiple experiences are selected"
              values={{ count: selected.length }}
            />
          )
        }
        open={open}
        popupKind="listbox"
        onClick={() => {
          setOpen((wasOpen) => {
            if (!wasOpen) onOpen?.();
            return !wasOpen;
          });
        }}
      />

      <ChoiceMenu
        anchor={triggerRef}
        open={open}
        onOpenChange={setOpen}
        placement="bottom start"
        multiple
        value={value}
        onChange={onChange}
        items={options.map((o) => ({ value: o.id, label: o.name }))}
        aria-label={intl.formatMessage({
          defaultMessage: 'Select experiences',
          description:
            'Aria label for the upcoming events experience filter listbox',
        })}
      />
    </>
  );
}
